"use client";
import { Portal } from "@ark-ui/react/portal";
import {
  Toaster as ArkToaster,
  createToaster,
  Toast,
  type ToastOptions,
  useToastContext,
} from "@ark-ui/react/toast";
import { CheckCircleIcon, CircleAlertIcon, CircleXIcon } from "lucide-react";
import { forwardRef } from "react";
import { createStyleContext, Stack, styled } from "@repo/styled-system/jsx";
import { toast } from "@repo/styled-system/recipes";
import { CloseButton, type CloseButtonProps } from "../close-button/close-button";
import { Icon, type IconProps } from "../icon/icon";
import { Spinner } from "../spinner/spinner";

const { withProvider, withContext } = createStyleContext(toast);

const Root = withProvider(Toast.Root, "root");
const Title = withContext(Toast.Title, "title");
const Description = withContext(Toast.Description, "description");
const ActionTrigger = withContext(Toast.ActionTrigger, "actionTrigger");
const CloseTrigger = withContext(Toast.CloseTrigger, "closeTrigger");
const StyledToaster = styled(ArkToaster);

const iconMap: Record<string, React.ElementType> = {
  warning: CircleAlertIcon,
  success: CheckCircleIcon,
  error: CircleXIcon,
};

const Indicator = forwardRef<SVGSVGElement, IconProps>((props, ref) => {
  const toastApi = useToastContext();

  const StatusIcon = iconMap[toastApi.type];
  if (!StatusIcon) return null;

  return (
    <Icon ref={ref} data-type={toastApi.type} {...props}>
      <StatusIcon />
    </Icon>
  );
});

// Annotate with the portable return type: the inferred type otherwise names
// `ToastStore` from a non-portable @zag-js/toast store path (TS2883 under pnpm on CI).
export const toaster: ReturnType<typeof createToaster> = createToaster({
  placement: "bottom-end",
  pauseOnPageIdle: true,
  overlap: true,
  max: 5,
});

export const Toaster = () => {
  return (
    <Portal>
      <StyledToaster toaster={toaster} insetInline={{ mdDown: "4" }}>
        {(toastItem: ToastOptions) => (
          <Root>
            {toastItem.type === "loading" ? (
              <Spinner color="colorPalette.plain.fg" />
            ) : (
              <Indicator />
            )}

            <Stack gap="3" alignItems="start">
              <Stack gap="1">
                {toastItem.title && <Title>{toastItem.title}</Title>}
                {toastItem.description && <Description>{toastItem.description}</Description>}
              </Stack>
              {toastItem.action && <ActionTrigger>{toastItem.action.label}</ActionTrigger>}
            </Stack>
            {toastItem.closable && (
              <CloseTrigger asChild>
                <CloseButton {...({ size: "sm" } as CloseButtonProps)} />
              </CloseTrigger>
            )}
          </Root>
        )}
      </StyledToaster>
    </Portal>
  );
};
